import { useRef, useState } from 'react';
import { useApp } from '../App.jsx';
import { Card, ConfirmDialog, Dialog, Field, Icon, Money, MonthSelect, PersonDot, Seg, Toggle, download, readFileText } from './ui.jsx';
import { SalarySummary, SalaryDialog, PersonDialog, normalMonth } from './salary.jsx';
import ImportDialog from './ImportDialog.jsx';
import { buildTemplate, buildReport, reportFileName, templateFileName } from '../lib/report.js';
import { XLSX_MIME } from '../lib/xlsx.js';
import { monthName, monthLabel } from '../lib/calendar.js';
import { parseMoney } from '../lib/money.js';
import { updateSettings, setTaxYear, removeTaxYear, applyImport } from '../lib/actions.js';
import { BUILTIN_YEARS, taxParamsFor, validateParams } from '../lib/tax/params.js';
import { initialState, looksLikeState, migrate, versionAt } from '../lib/state.js';
import { asideCopies } from '../store.js';
import { buildModel } from '../lib/model.js';
import { monthsFromImport } from '../lib/sheetImport.js';

function SalaryRow({ person }) {
  const { data, model } = useApp();
  const [dialog, setDialog] = useState(null);
  const plan = versionAt(person.plans, model.current) || person.plans[0];
  const nm = plan ? normalMonth(plan, person, data.taxYears, plan.from > model.current ? plan.from : model.current) : null;
  return (
    <div className="salary-row">
      <div className="row wrap" style={{ marginBottom: 6 }}>
        <span className="pill-person" style={{ fontSize: 15 }}><PersonDot color={person.color} />{person.name}</span>
        {plan?.label && <span className="small muted">{plan.label}</span>}
        <span className="spacer" />
        {nm && <span className="small">Normal month to the bank: <b><Money value={nm.net} /></b></span>}
      </div>
      <SalarySummary person={person} compact />
      <div className="row wrap" style={{ marginTop: 8 }}>
        <button type="button" className="btn small primary" onClick={() => setDialog('edit')}><Icon name="edit" />Change salary</button>
        <button type="button" className="btn small" onClick={() => setDialog('raise')}>Got a raise?</button>
        <button type="button" className="btn small ghost" onClick={() => setDialog('about')}>About {person.name}</button>
      </div>
      {(dialog === 'edit' || dialog === 'raise') && <SalaryDialog person={person} mode={dialog} onClose={() => setDialog(null)} />}
      {dialog === 'about' && <PersonDialog person={person} onClose={() => setDialog(null)} />}
    </div>
  );
}

function SheetRow({ person }) {
  const { data, model, api, sheets, loadSheets, toast, commit, today } = useApp();
  const [linking, setLinking] = useState(false);
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [unlinking, setUnlinking] = useState(false);
  const fileRef = useRef(null);
  const [parsed, setParsed] = useState(null);
  const s = sheets?.[person.id];

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast('That file is too big (max 5 MB)', { kind: 'error' }); return; }
    setBusy(true);
    try {
      const res = await api.readSpreadsheet(file);
      setParsed({ ...res, fileName: file.name });
    } catch (e) {
      toast(e.message || "Couldn't read that file", { kind: 'error', ms: 9000 });
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };
  const link = async () => {
    setBusy(true); setError(null);
    try { await api.linkSheet(person.id, url); setLinking(false); setUrl(''); loadSheets(); toast(`Linked ${person.name}'s spreadsheet`); } catch (e) { setError(e.message); } finally { setBusy(false); }
  };
  const sync = async () => {
    setBusy(true);
    try { await api.syncSheet(person.id); loadSheets(); } catch (e) { toast(e.message, { kind: 'error' }); } finally { setBusy(false); }
  };
  const unlink = async (keep) => {
    if (keep && (s?.rows?.length || s?.entries?.length)) {
      const changes = monthsFromImport(s, person).flatMap((r) => Object.entries(r.values || {}).map(([field, to]) => (Array.isArray(to)
        ? { month: r.month, field, to: to.reduce((a, x) => a + x.amount, 0), items: to } : { month: r.month, field, to }))).filter((c) => model.byKey[c.month]);
      commit(`Keep ${person.name}'s spreadsheet values`, (st) => applyImport(st, buildModel(st, { today, sheets: {} }), person.id, changes));
    }
    try { await api.unlinkSheet(person.id); loadSheets(); toast('Spreadsheet unlinked'); } catch (e) { toast(e.message, { kind: 'error' }); }
    setUnlinking(false);
  };

  return (
    <div className="list-row" style={{ alignItems: 'flex-start' }}>
      <span className="pill-person" style={{ minWidth: 110, marginTop: 6 }}><PersonDot color={person.color} />{person.name}</span>
      <div className="stack tight" style={{ flex: 1, minWidth: 240 }}>
        <div className="row wrap">
          <button type="button" className="btn small" title={`A table for ${person.name} to fill in (date, type, description, amount) and upload back`} onClick={() => download(templateFileName(person.name), buildTemplate(data, model, person.id), XLSX_MIME)}><Icon name="download" />Template</button>
          <button type="button" className="btn small" disabled={busy} onClick={() => fileRef.current?.click()}><Icon name="upload" />Upload filled spreadsheet</button>
          <input ref={fileRef} type="file" accept=".xlsx,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv" hidden aria-label={`Spreadsheet for ${person.name}`} onChange={(e) => upload(e.target.files?.[0])} />
          {!s && <button type="button" className="btn small" onClick={() => setLinking(true)}><Icon name="link" />Link a Google Sheet</button>}
        </div>
        {s && (
          <div className="small">
            <span className="badge sheet">Linked</span> <span className="dim">{s.link}</span>
            {' · '}{s.fetchedAt ? `read ${new Date(s.fetchedAt).toLocaleString()}` : 'never read'}{' · '}{s.kind === 'log' ? `${s.entries.length} rows` : `${s.rows.length} months`}
            {s.error && <div style={{ color: 'var(--danger)', marginTop: 4 }}>Last try failed: {s.error}</div>}
            {s.warnings?.length > 0 && <details className="more" style={{ marginTop: 4 }}><summary>{s.warnings.length} rows skipped</summary><ul style={{ margin: 0, paddingLeft: 18 }}>{s.warnings.slice(0, 10).map((w, i) => <li key={i}>{w}</li>)}</ul></details>}
            {s.metaPerson && s.metaPerson !== person.id && <div style={{ color: 'var(--warn)' }}>This sheet was made from another person's template.</div>}
            <div className="row" style={{ marginTop: 6 }}>
              <button type="button" className="btn small" disabled={busy} onClick={sync}><Icon name="refresh" />Read now</button>
              <button type="button" className="btn ghost small danger" onClick={() => setUnlinking(true)}>Unlink</button>
            </div>
          </div>
        )}
      </div>
      {linking && (
        <Dialog title={`Link ${person.name}'s Google Sheet`} onClose={() => setLinking(false)} footer={<><span className="spacer" /><button type="button" className="btn" onClick={() => setLinking(false)}>Cancel</button><button type="button" className="btn primary" disabled={busy || !url.trim()} onClick={link}>{busy ? 'Checking…' : 'Link'}</button></>}>
          <ol className="dim" style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 4 }}>
            <li>Download the template above and upload it to Google Drive (or start from any sheet with a Month column).</li>
            <li>Open it with Google Sheets: <i>File → Save as Google Sheets</i>.</li>
            <li><i>Share → General access → Anyone with the link → Viewer</i>. (Or <i>File → Share → Publish to web</i>.)</li>
            <li>Copy the link and paste it here.</li>
          </ol>
          <p className="small muted">The app reads it every 10 minutes and whenever someone opens the app. Values in the sheet win over the app for that person; months with a take-home amount count as reviewed. Anyone with the link can see the sheet, so don't post it anywhere.</p>
          <Field label="Link" error={error}><input className="input" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://docs.google.com/spreadsheets/d/…" autoFocus /></Field>
        </Dialog>
      )}
      {unlinking && (
        <ConfirmDialog title="Unlink the spreadsheet?" confirmLabel="Unlink and keep values" cancelLabel="Cancel" onCancel={() => setUnlinking(false)} onConfirm={() => unlink(true)}>
          <p className="dim">The values read from it can be copied into the app so nothing changes, or dropped so those paydays go back to “to review”.</p>
          <button type="button" className="btn danger" onClick={() => unlink(false)}>Unlink and drop its values</button>
        </ConfirmDialog>
      )}
      {parsed && <ImportDialog parsed={parsed} fileName={parsed.fileName} onClose={() => setParsed(null)} />}
    </div>
  );
}

const num = (v) => { const r = parseMoney(String(v), { allowNegative: false }); return r.ok ? r.value : NaN; };
// A number box that saves when you leave it (defined here, not inside a component, so it keeps focus)
function NumIn({ value, onChange, label, w = 90 }) {
  return <input className="input money" style={{ width: w }} aria-label={label} defaultValue={value ?? ''} onBlur={(e) => onChange(e.target.value === '' ? null : num(e.target.value))} />;
}

// Edit the figures of one tax year (for when the new year's tables come out)
function TaxYearEditor({ year, onClose }) {
  const { data, commit } = useApp();
  const base = data.taxYears[year] || taxParamsFor(year, data.taxYears).params;
  const [p, setP] = useState(() => JSON.parse(JSON.stringify({ ...base, year })));
  const [error, setError] = useState(null);
  const setRow = (table, i, patch) => setP((x) => {
    const rows = x.withholding[table].rows.map((r, j) => (j === i ? { ...r, ...patch } : r));
    const withholding = { ...x.withholding, [table]: { ...x.withholding[table], rows } };
    if (table === 'I') withholding.II = { ...withholding.II, rows };
    return { ...x, withholding };
  });
  const save = () => {
    const errs = validateParams(p);
    if (errs.length) { setError(errs.join(' · ')); return; }
    const res = commit(`Tax rules for ${year}`, (s) => setTaxYear(s, year, p));
    if (res.error) setError(res.error); else onClose();
  };
  const rowsEditor = (table) => (
    <table className="plain">
      <thead><tr><th>Pay up to</th><th>Rate %</th><th>Subtract</th><th className="left">or formula k × (ref − R)</th></tr></thead>
      <tbody>
        {p.withholding[table].rows.map((r, i) => (
          <tr key={`${table}${i}`}>
            <td>{i === p.withholding[table].rows.length - 1 ? 'above' : <NumIn label={`Table ${table} row ${i + 1} limit`} value={r.upTo} onChange={(v) => setRow(table, i, { upTo: v })} />}</td>
            <td><NumIn label={`Table ${table} row ${i + 1} rate`} value={r.rate} w={70} onChange={(v) => setRow(table, i, { rate: v })} /></td>
            <td>{typeof r.deduct === 'number' ? <NumIn label={`Table ${table} row ${i + 1} subtract`} value={r.deduct} onChange={(v) => setRow(table, i, { deduct: v })} /> : <span className="muted">formula</span>}</td>
            <td className="left">{typeof r.deduct === 'object' && r.deduct ? (
              <span className="row"><NumIn label="k" w={60} value={r.deduct.k} onChange={(v) => setRow(table, i, { deduct: { ...r.deduct, k: v } })} /><NumIn label="ref" w={90} value={r.deduct.ref} onChange={(v) => setRow(table, i, { deduct: { ...r.deduct, ref: v } })} /></span>
            ) : null}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
  return (
    <Dialog wide title={`Tax rules for ${year}`} onClose={onClose} footer={<><span className="spacer" /><button type="button" className="btn" onClick={onClose}>Cancel</button><button type="button" className="btn primary" onClick={save}>Save {year}</button></>}>
      <div className="banner info"><Icon name="info" className="icon" /><div>New figures come out every January: the withholding tables in a <i>Despacho</i> in Diário da República, and the brackets in the State Budget law. Copy them here when they're published.</div></div>
      {error && <div className="banner danger" role="alert"><Icon name="alert" className="icon" /><div>{error}</div></div>}
      <div className="grid3">
        <Field label="IAS"><NumIn label="IAS" value={p.ias} onChange={(v) => setP({ ...p, ias: v })} w="100%" /></Field>
        <Field label="Minimum wage"><NumIn label="Minimum wage" value={p.minWage} onChange={(v) => setP({ ...p, minWage: v })} w="100%" /></Field>
        <Field label="Mínimo de existência"><NumIn label="Mínimo de existência" value={p.minimoExistencia} onChange={(v) => setP({ ...p, minimoExistencia: v })} w="100%" /></Field>
        <Field label="Meal card tax-free/day"><NumIn label="Meal card limit" value={p.mealExempt.card} onChange={(v) => setP({ ...p, mealExempt: { ...p.mealExempt, card: v } })} w="100%" /></Field>
        <Field label="Meal in cash tax-free/day"><NumIn label="Meal cash limit" value={p.mealExempt.cash} onChange={(v) => setP({ ...p, mealExempt: { ...p.mealExempt, cash: v } })} w="100%" /></Field>
        <Field label="Social security %"><NumIn label="Social security" value={p.socialSecurity.employee} onChange={(v) => setP({ ...p, socialSecurity: { ...p.socialSecurity, employee: v } })} w="100%" /></Field>
      </div>
      <details className="more" open>
        <summary>Yearly brackets (art. 68.º)</summary>
        <table className="plain">
          <thead><tr><th>Taxable income up to</th><th>Rate %</th><th>Average rate % (from the law)</th></tr></thead>
          <tbody>
            {p.brackets.map((b, i) => (
              <tr key={i}>
                <td>{i === p.brackets.length - 1 ? 'above' : <NumIn label={`Bracket ${i + 1} limit`} value={b.upTo} onChange={(v) => setP({ ...p, brackets: p.brackets.map((x, j) => (j === i ? { ...x, upTo: v } : x)) })} />}</td>
                <td><NumIn label={`Bracket ${i + 1} rate`} value={b.rate} w={70} onChange={(v) => setP({ ...p, brackets: p.brackets.map((x, j) => (j === i ? { ...x, rate: v } : x)) })} /></td>
                <td>{i === p.brackets.length - 1 ? '' : <NumIn label={`Bracket ${i + 1} average`} value={b.avg} w={80} onChange={(v) => setP({ ...p, brackets: p.brackets.map((x, j) => (j === i ? { ...x, avg: v ?? undefined } : x)) })} />}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </details>
      <details className="more">
        <summary>Withholding table I (and II) — single, or married with both earning</summary>
        {rowsEditor('I')}
      </details>
      <details className="more">
        <summary>Withholding table III — married, only one earning</summary>
        {rowsEditor('III')}
      </details>
    </Dialog>
  );
}

export default function Settings() {
  const { data, model, commit, store, today, me, setMe, auth, api, checkAuth, toast } = useApp();
  const [taxYear, setTaxYearOpen] = useState(null);
  const [resetting, setResetting] = useState(false);
  const [restore, setRestore] = useState(null);
  const restoreRef = useRef(null);
  const st = data.settings;
  const set = (patch, label) => commit(label, (s) => updateSettings(s, patch));
  const thisYear = Number(today.slice(0, 4));
  const years = [...new Set([...Object.keys(BUILTIN_YEARS), ...Object.keys(data.taxYears)].map(Number))].sort();
  const missing = [thisYear, thisYear + 1].filter((y) => !years.includes(y));

  const onRestoreFile = async (file) => {
    if (!file) return;
    try {
      const json = JSON.parse(await readFileText(file));
      const candidate = json?.data && looksLikeState(json.data) ? json.data : json;
      if (!looksLikeState(candidate)) throw new Error("That file isn't a Joint Savings backup");
      setRestore({ data: migrate(candidate), name: file.name });
    } catch (e) {
      toast(e.message.startsWith('Unexpected') ? "That file isn't a Joint Savings backup" : e.message, { kind: 'error' });
    } finally {
      if (restoreRef.current) restoreRef.current.value = '';
    }
  };
  const aside = asideCopies();

  return (
    <div className="stack" style={{ gap: 18 }}>
      <div className="page-head"><div><h1>Settings</h1><p>Salaries, spreadsheets, sharing and backups.</p></div></div>

      <Card title="Salaries & people">
        <p className="dim" style={{ marginBottom: 4 }}>Got a raise, changed jobs or got your first payslip? Change it here; the months before keep their old numbers.</p>
        {data.people.filter((p) => !p.until).map((p) => <SalaryRow key={p.id} person={p} />)}
      </Card>

      <Card title="Spreadsheets">
        <p className="dim" style={{ marginBottom: 8 }}>For whoever prefers Excel or Google Sheets. Their template is one simple table: each row is money that came in or an extra expense (date, type, description, amount). Upload it back here now and then, or link it as a Google Sheet so the app reads it by itself. Either way it shows up in all the statistics.</p>
        {data.people.filter((p) => !p.until).map((p) => <SheetRow key={p.id} person={p} />)}
      </Card>

      <div className="grid2" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))' }}>
        <Card title="Export">
          <div className="stack">
            <div className="row wrap">
              <button type="button" className="btn" onClick={() => download(reportFileName(today), buildReport(data, model), XLSX_MIME)}><Icon name="sheet" />Excel report</button>
              <button type="button" className="btn" onClick={() => download(`joint-savings-backup-${today}.json`, JSON.stringify({ app: 'joint-savings', exportedAt: new Date().toISOString(), data }, null, 2), 'application/json')}><Icon name="download" />Backup (.json)</button>
            </div>
            <p className="small muted">The Excel report is for reading: every month, payslip, expense and IRS year. It can't be uploaded back (that's what each person's Template is for). The server also keeps a copy of the data for each of the last 60 days.</p>
            <div className="divider" />
            <div className="row wrap">
              <button type="button" className="btn" onClick={() => restoreRef.current?.click()}><Icon name="upload" />Restore a backup…</button>
              <input ref={restoreRef} type="file" accept=".json,application/json" hidden aria-label="Backup file" onChange={(e) => onRestoreFile(e.target.files?.[0])} />
            </div>
            {aside.length > 0 && (
              <details className="more">
                <summary>Copies kept aside on this device ({aside.length})</summary>
                {aside.map((a) => (
                  <div className="list-row" key={a.key}>
                    <span className="small">{new Date(a.at).toLocaleString()}</span><span className="spacer" />
                    <button type="button" className="btn small" onClick={() => { try { download(`joint-savings-aside-${a.at}.json`, localStorage.getItem(a.key), 'application/json'); } catch {} }}>Download</button>
                    <button type="button" className="btn ghost small danger" onClick={() => { try { localStorage.removeItem(a.key); toast('Removed'); } catch {} }}>Remove</button>
                  </div>
                ))}
              </details>
            )}
          </div>
        </Card>

        <Card title="Access">
          <div className="stack">
            {auth.required ? (
              <>
                <p className="dim">Google sign-in is on. Signed in as <b>{auth.user?.email}</b>. Anyone whose email is set on a person (Salaries & people → About) can sign in, plus the server's ALLOWED_EMAILS.</p>
                <button type="button" className="btn" style={{ alignSelf: 'flex-start' }} onClick={async () => { await api.logout().catch(() => {}); checkAuth(); }}><Icon name="logout" />Sign out</button>
              </>
            ) : (
              <p className="dim">Anyone who can reach this address can use the app (no sign-in). That's fine on a private network like Tailscale: invite the others to it. To require Google sign-in, set GOOGLE_CLIENT_ID on the server — see the README.</p>
            )}
            <Field label="This device is used by">
              <select className="input" value={me || ''} onChange={(e) => setMe(e.target.value)}>
                <option value="">—</option>
                {data.people.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </Field>
          </div>
        </Card>
      </div>

      <details className="card more-card">
        <summary><b>Advanced</b> <span className="small muted">— number format, what counts as savings, tax rules, activity, start over</span></summary>
        <div className="stack" style={{ gap: 18, marginTop: 14 }}>
      <Card title="Preferences">
        <div className="grid3">
          <Field label="Number format" group>
            <Seg ariaLabel="Number format" value={st.numberLocale} onChange={(v) => set({ numberLocale: v }, 'Number format')} options={[{ value: 'pt-PT', label: '1 234,56 €' }, { value: 'en-GB', label: '€1,234.56' }]} />
          </Field>
          <Field label="First month tracked" hint="Earlier months are hidden, not deleted" group>
            <MonthSelect ariaLabel="First month tracked" value={st.startMonth} onChange={(v) => v && set({ startMonth: v }, `Start tracking in ${monthLabel(v)}`)} />
          </Field>
          <Field label="Months to look ahead">
            <select className="input" value={st.horizon} onChange={(e) => set({ horizon: Number(e.target.value) }, 'Look-ahead')}>{[6, 12, 24, 36, 60, 120].map((n) => <option key={n} value={n}>{n} months</option>)}</select>
          </Field>
        </div>
        <div className="stack" style={{ marginTop: 14 }}>
          <p className="small muted">Savings count the take-home pay that reaches the bank. Expenses paid back never count.</p>
          <Toggle checked={st.countMealCard !== false} onChange={(v) => set({ countMealCard: v }, `Meal card ${v ? 'counts' : "doesn't count"} towards savings`)} label="Also count the meal card (normally off: it only pays for food)" />
          <Toggle checked={st.countAllowance !== false} onChange={(v) => set({ countAllowance: v }, `Fixed allowances ${v ? 'count' : "don't count"} towards savings`)} label="Also count fixed allowances paid every month with the salary (rare — not the Despesas)" />
          <div className="row wrap">
            <Toggle checked={st.irsRefund?.include !== false} onChange={(v) => set({ irsRefund: { ...st.irsRefund, include: v } }, `IRS refunds ${v ? 'included' : 'left out'}`)} label="Expect each year's IRS refund in" />
            <select className="input" style={{ width: 140 }} aria-label="Refund month" value={st.irsRefund?.month || 5} onChange={(e) => set({ irsRefund: { ...st.irsRefund, month: Number(e.target.value) } }, 'IRS refund month')}>{Array.from({ length: 12 }, (_, i) => <option key={i + 1} value={i + 1}>{monthName(i + 1)}</option>)}</select>
          </div>
        </div>
      </Card>

      <Card title="Tax rules" actions={missing.length > 0 && <button type="button" className="btn small" onClick={() => setTaxYearOpen(missing[missing.length - 1])}><Icon name="plus" />Add {missing[missing.length - 1]}</button>}>
        <p className="dim" style={{ marginBottom: 8 }}>Years without their own figures use the latest year available, and their numbers are marked as estimates.</p>
        {years.map((y) => (
          <div className="list-row" key={y}>
            <b>{y}</b>
            <span className="small muted">{data.taxYears[y] ? 'added here' : BUILTIN_YEARS[y]?.source}</span>
            <span className="spacer" />
            <button type="button" className="btn small" onClick={() => setTaxYearOpen(y)}>{data.taxYears[y] ? 'Edit' : 'View / override'}</button>
            {data.taxYears[y] && <button type="button" className="btn ghost small danger" onClick={() => commit(`Remove tax rules for ${y}`, (s) => removeTaxYear(s, y), { undoToast: true })}>Remove</button>}
          </div>
        ))}
        {missing.map((y) => <div className="list-row" key={y}><span className="muted">{y}</span><span className="small muted">using {taxParamsFor(y, data.taxYears).sourceYear}'s figures (estimate)</span></div>)}
      </Card>

      <Card title="Activity">
        {!(data.log || []).length ? <p className="muted">No changes yet.</p> : (
          <div style={{ maxHeight: 320, overflow: 'auto' }}>
            {[...data.log].reverse().map((l) => (
              <div className="list-row" key={l.id}>
                <span className="small muted nowrap">{new Date(l.at).toLocaleString()}</span>
                <span className="small">{data.people.find((p) => p.id === l.by)?.name || '—'}</span>
                <span style={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>{l.label}</span>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card title="Start over">
        <p className="dim" style={{ marginBottom: 8 }}>Deletes everything for everyone. Download a backup first. (It can still be undone from this device until the page is closed.)</p>
        <button type="button" className="btn danger" onClick={() => setResetting(true)}>Delete all data…</button>
      </Card>
        </div>
      </details>

      {taxYear && <TaxYearEditor year={taxYear} onClose={() => setTaxYearOpen(null)} />}
      {resetting && <ConfirmDialog title="Delete all data?" message="Every person, salary, expense, payday review and saving entry goes, on every device." danger typeToConfirm="DELETE" confirmLabel="Delete everything" onCancel={() => setResetting(false)} onConfirm={() => { commit('Delete all data', () => initialState(st.startMonth)); setResetting(false); toast('Everything was reset', { action: { label: 'Undo', fn: () => store.undo() } }); }} />}
      {restore && (
        <ConfirmDialog title={`Restore ${restore.name}?`} danger confirmLabel="Replace everything" onCancel={() => setRestore(null)} onConfirm={() => { store.replaceAll(restore.data); setRestore(null); toast('Backup restored', { action: { label: 'Undo', fn: () => store.undo() } }); }}>
          <p className="dim">The current data is replaced by the backup, on every device: {restore.data.people.length} people, {restore.data.expenses.length} expenses, {Object.keys(restore.data.months).length} months with entries, {restore.data.savings.transactions.length} savings entries.</p>
        </ConfirmDialog>
      )}
    </div>
  );
}
